import React, { FunctionComponent, ReactNode, useRef, useState } from 'react'
import styles from './style.module.css'
import { CARET_PLACEMENTS, Heading, Icon, Text, Tooltip, useIsBreakpointMediumAndUp, usePercent } from '@afterpaytouch/core'
import { Trans, useTranslation } from 'next-i18next'
import { useCheckoutSizes } from '../../utils/responsive'
import { TrackingEvent } from '../../model/monitoring'
import { useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import { useAppRouter } from '../../state'
import clsx from 'clsx'

interface InterestRateProps {
  aprMax?: number
  aprMin?: number
}

export const ApprovedCreditInterestRate: FunctionComponent<InterestRateProps> = ({ aprMax, aprMin }) => {
  const { t } = useTranslation()
  const { smallTextSize, textSize } = useCheckoutSizes()
  const [showTooltip, setShowTooltip] = useState(false)
  const buttonRef = useRef(null)
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const [hasSentAnalyticsData, setHasSentAnalyticsData] = useState(false)
  const { locale } = useAppRouter()
  const { formatPercent } = usePercent({ locale, decimalPlaces: 2, shouldDivideBy100: true })
  const isDesktop = useIsBreakpointMediumAndUp() ?? false
  const [hasClickedOutside, setHasClickedOutside] = useState<boolean>(false)
  const id = 'interest-rate-tooltip'

  const toggleTooltip = (): void => {
    if (!hasSentAnalyticsData) {
      logEvent(TrackingEvent.PRESSED_CL_APPROVED_CREDIT_PAGE_INTEREST_RANGE_TOOLTIP)
      setHasSentAnalyticsData(true)
    }
    if (!hasClickedOutside) {
      setShowTooltip(!showTooltip)
    }
    setHasClickedOutside(false)
  }

  const checkIfOutsideClick = (): void => {
    setHasClickedOutside(false)
  }

  const handleTooltipClose = (): void => {
    setShowTooltip(false)
  }

  const toolTip = (): ReactNode => (
    <Text size={smallTextSize} testNameSpace='interest-rate-terms'>
      {t('consumerLending:approvedCredit.interestRateRange.tooltip')}
    </Text>
  )

  return (
    <div className={styles.approvedCreditBodyHeader}>
      <Heading size={textSize}>{t('consumerLending:approvedCredit.interestRateRange.heading')}</Heading>
      <div className={styles.approvedCreditBodyContent}>
        <Text size={'S'}>
          <Trans
            i18nKey={'consumerLending:approvedCredit.interestRateRange.rateTo'}
            values={{
              aprMin: formatPercent(Number(aprMin)),
              aprMax: formatPercent(Number(aprMax)),
            }}
          />
        </Text>
        <button
          className={clsx(styles.tooltipButtonBase, styles.tooltipButtonInterestRate)}
          type='button'
          onClick={toggleTooltip}
          onMouseDown={checkIfOutsideClick}
          data-testid='interest-rate-info-button'
          ref={buttonRef}
          aria-label={t('consumerLending:approvedCredit.interestRateRange.ariaLabel')}
          aria-describedby={id}
          aria-expanded={showTooltip}
        >
          <Icon.Info size='ExtraSmall' />
        </button>
      </div>
      {showTooltip && (
        <Tooltip
          id={id}
          triggerRef={buttonRef}
          caretPlacement={CARET_PLACEMENTS.BOTTOM_START}
          testNameSpace='interest-rate-tooltip'
          usePortal={false}
          useMobileView={!isDesktop}
          content={toolTip()}
          skidding={145}
          onClose={handleTooltipClose}
          closeOnOutsideClick={setHasClickedOutside}
        />
      )}
    </div>
  )
}
